// skills/husbandry/husbandryUI.js
// Husbandry UI rendering and interaction - Pet management interface

import { husbandrySkill } from './husbandry.js';
import {
    PETS,
    PET_CONTRACTS,
    EVOLUTION_BONUSES,
    FEED_CONFIG,
    getFeedSpeedBonus,
    getMaxFood,
    getRequiredFood,
    getEvolutionCost,
    getPetData,
    getContractsForPet,
    getContractTierRewards,
    CONTRACT_TIER_UPGRADES,
    HUSBANDRY_CONFIG
} from './husbandryData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS, GAME_CONFIG, PET_CONFIG } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatNumber, formatTime, formatWithCommas } from '../../utils/format.js';
import { getItemName, getItemColor, getItemIcon } from '../../data/items/index.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { skillRegistry } from '../../core/skillRegistry.js';
import { getXPPerHour } from '../../systems/experience.js';
import { renderInfoIcon } from '../../ui/infoTooltip.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { QUESTS } from '../../data/quests/questData.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { rollGoldenOpportunity } from '../pilfering/pilfering.js';
import { getNodeRank } from '../../systems/talentTree/talentTree.js';

// Current tab state - persists across navigation
let currentTab = 'pets';

/**
 * Get the current tab for persistence
 */
export function getCurrentTab() {
    return currentTab;
}

// Track selected pet for management modal
let selectedPetId = null;

// Track current tab in pet management modal
let petModalTab = 'contracts'; // 'contracts', 'feed', 'awaken'

// Track chest fill estimate for countdown
let chestFillEstimate = {
    targetTime: null,       // When we estimate the chest will be full
    lastCalculatedAt: null, // When we last recalculated
    slotsAtCalculation: 0,  // Slots remaining when calculated
    activePets: 0           // Active count when calculated
};

/**
 * Format time remaining in ms to readable string
 */
function formatTimeRemaining(ms) {
    if (ms === null || ms <= 0) return 'Ready!';
    const seconds = Math.ceil(ms / 1000);
    return formatTime(seconds);
}

/**
 * Find the quest number that provides a specific unlock
 */
function findQuestByUnlock(unlockId) {
    for (const [questNum, quest] of Object.entries(QUESTS)) {
        if (quest.rewards?.unlocks?.includes(unlockId)) {
            return parseInt(questNum);
        }
    }
    return null;
}

/**
 * Calculate chest fill timer estimate
 * Uses total capacity (slots × stack limit) for accurate estimates
 * @returns {{ timeUntilFull: number|null, nextCompletion: number|null, capacityRemaining: number, activePets: number, totalCapacity: number, currentFill: number }}
 */
function calculateChestFillTimer() {
    const chestStats = husbandrySkill.getLootChestStats();
    const maxStack = gameState.getLootChestMaxStack();

    // Calculate total capacity and current fill
    const totalCapacity = chestStats.maxSlots * maxStack;
    const chest = gameState.getLootChest();
    const currentFill = chest.reduce((sum, slot) => sum + slot.quantity, 0);
    const capacityRemaining = totalCapacity - currentFill;

    // Get all active pets and their contract info (both slots)
    const pets = gameState.getPets();
    const activePetData = [];

    for (const pet of pets) {
        if (!pet.unlocked) continue;

        const speedBonus = husbandrySkill.getPetSpeedBonus(pet.id);

        // Process primary contract
        if (pet.contractId) {
            const contract = PET_CONTRACTS[pet.contractId];
            if (contract) {
                const rawTimeRemaining = husbandrySkill.getContractTimeRemaining(pet.id, 'primary');
                const effectiveDuration = contract.duration * (1 - speedBonus);
                const timeRemaining = rawTimeRemaining <= 0 ? effectiveDuration : Math.max(1000, rawTimeRemaining);
                const avgItemsPerCompletion = contract.rewards.reduce((sum, r) => sum + (r.chance * ((r.minQty + r.maxQty) / 2)), 0);
                activePetData.push({ petId: pet.id, contractId: pet.contractId, timeRemaining, effectiveDuration, avgItemsPerCompletion: Math.max(1, Math.ceil(avgItemsPerCompletion)) });
            }
        }

        // Process secondary contract
        if (pet.secondaryContractId) {
            const contract = PET_CONTRACTS[pet.secondaryContractId];
            if (contract) {
                const rawTimeRemaining = husbandrySkill.getContractTimeRemaining(pet.id, 'secondary');
                const effectiveDuration = contract.duration * (1 - speedBonus);
                const timeRemaining = rawTimeRemaining <= 0 ? effectiveDuration : Math.max(1000, rawTimeRemaining);
                const avgItemsPerCompletion = contract.rewards.reduce((sum, r) => sum + (r.chance * ((r.minQty + r.maxQty) / 2)), 0);
                activePetData.push({ petId: pet.id, contractId: pet.secondaryContractId, timeRemaining, effectiveDuration, avgItemsPerCompletion: Math.max(1, Math.ceil(avgItemsPerCompletion)) });
            }
        }
    }

    if (activePetData.length === 0) {
        chestFillEstimate.targetTime = null;
        return {
            timeUntilFull: null,
            nextCompletion: null,
            capacityRemaining,
            activePets: 0,
            totalCapacity,
            currentFill
        };
    }

    // Find next completion
    const nextCompletion = Math.min(...activePetData.map(p => p.timeRemaining).filter(t => t > 0));

    // If chest is already full (all slots at max stack)
    if (capacityRemaining <= 0) {
        chestFillEstimate.targetTime = null;
        return {
            timeUntilFull: 0,
            nextCompletion: nextCompletion > 0 ? nextCompletion : null,
            capacityRemaining: 0,
            activePets: activePetData.length,
            totalCapacity,
            currentFill
        };
    }

    const now = Date.now();

    // Recalculate target time if conditions changed OR estimate expired
    const estimateExpired = chestFillEstimate.targetTime && chestFillEstimate.targetTime <= now;
    const needsRecalc = !chestFillEstimate.targetTime ||
        estimateExpired ||
        capacityRemaining !== chestFillEstimate.slotsAtCalculation ||
        activePetData.length !== chestFillEstimate.activePets;

    if (needsRecalc) {
        // Calculate time based on total items needed to fill capacity
        // Sort pets by time remaining to simulate order of completions
        const sortedPets = [...activePetData].sort((a, b) => a.timeRemaining - b.timeRemaining);

        let timeToFill = 0;
        let itemsToFill = capacityRemaining;
        let simulatedTime = 0;

        // Simulate filling capacity by walking through expected completions
        while (itemsToFill > 0 && sortedPets.length > 0) {
            const nextPet = sortedPets[0];
            const timeToNextCompletion = Math.max(0, nextPet.timeRemaining - simulatedTime);

            simulatedTime += timeToNextCompletion;
            timeToFill = simulatedTime;

            // Each completion adds items
            itemsToFill -= nextPet.avgItemsPerCompletion;

            // Reset this pet's timer to its full duration for next cycle
            nextPet.timeRemaining = simulatedTime + nextPet.effectiveDuration;

            // Re-sort for next iteration
            sortedPets.sort((a, b) => a.timeRemaining - b.timeRemaining);
        }

        if (timeToFill > 0) {
            chestFillEstimate.targetTime = now + timeToFill;
            chestFillEstimate.lastCalculatedAt = now;
            chestFillEstimate.slotsAtCalculation = capacityRemaining;
            chestFillEstimate.activePets = activePetData.length;
        } else {
            chestFillEstimate.targetTime = null;
        }
    }

    // Return countdown from target time
    const timeUntilFull = chestFillEstimate.targetTime
        ? Math.max(0, chestFillEstimate.targetTime - now)
        : null;

    return {
        timeUntilFull,
        nextCompletion: nextCompletion > 0 ? nextCompletion : null,
        capacityRemaining,
        activePets: activePetData.length,
        totalCapacity,
        currentFill
    };
}

/**
 * Format contract duration for display
 */
function formatDuration(ms) {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainMins = minutes % 60;
    if (remainMins === 0) return `${hours}h`;
    return `${hours}h ${remainMins}m`;
}

/**
 * Get XP display text based on contract type
 * Resource → Gatherer + Husbandry
 * Combat → Bounty + Husbandry
 * Purification → Husbandry only
 * Veil → Gatherer + Bounty + Husbandry (Eidolon specialty)
 */
function getContractXPDisplay(contract) {
    const xp = contract.xp;
    // Use getSkillIcon() for proper image-based icons (falls back to emoji if no iconImage)
    const husbandryIcon = skillRegistry.getSkillIcon(SKILLS.HUSBANDRY);
    const gathererIcon = skillRegistry.getSkillIcon(SKILLS.GATHERER);
    const bountyIcon = skillRegistry.getSkillIcon(SKILLS.BOUNTY);

    switch (contract.type) {
        case 'resource':
            return `${gathererIcon}${xp} XP ${husbandryIcon}${xp} XP`;
        case 'combat':
            return `${bountyIcon}${xp} XP ${husbandryIcon}${xp} XP`;
        case 'veil':
            // Veil Expeditions give 50% each to Gatherer and Bounty, plus full Husbandry
            const halfXp = Math.floor(xp * 0.5);
            return `${gathererIcon}${halfXp} ${bountyIcon}${halfXp} ${husbandryIcon}${xp} XP`;
        case 'purification':
        default:
            return `${husbandryIcon}${xp} XP`;
    }
}

/**
 * Get the icon for a contract type using skill icons
 * Resource → Gatherer icon, Combat → Combat icon, Purification → ✨, Veil → 👻
 */
function getContractTypeIcon(type) {
    switch (type) {
        case 'resource':
            return skillRegistry.getSkillIcon(SKILLS.GATHERER);
        case 'combat':
            return skillRegistry.getSkillIcon(SKILLS.COMBAT);
        case 'purification':
            return '✨';
        case 'veil':
            return '👻';
        default:
            return '📜';
    }
}

/**
 * Get talent synergy bonuses that a pet can find based on player talents
 * Returns array of { icon, name, skillName } for display
 */
function getPetTalentSynergies(petId) {
    const petData = getPetData(petId);
    if (!petData) return [];

    // Map pet roles to applicable skill trees (matches husbandry.js getTalentSynergyMapping)
    const petRoleToSkills = {
        gatherer: [SKILLS.WOODCUTTING, SKILLS.GATHERER, SKILLS.FARMING, SKILLS.PYRE],  // Rootling
        combat: [SKILLS.MINING, SKILLS.FISHING, SKILLS.PYRE],                          // Felltracker
        purifier: [SKILLS.ALCHEMY, SKILLS.GLYPHBINDING, SKILLS.PYRE],                  // Ichorebound
        hybrid: [SKILLS.WOODCUTTING, SKILLS.MINING, SKILLS.FISHING, SKILLS.GATHERER,
                 SKILLS.FARMING, SKILLS.ALCHEMY, SKILLS.PYRE, SKILLS.GLYPHBINDING],    // Chimera - all
        elite: [SKILLS.WOODCUTTING, SKILLS.MINING, SKILLS.FISHING, SKILLS.GATHERER,
                SKILLS.FARMING, SKILLS.ALCHEMY, SKILLS.PYRE, SKILLS.GLYPHBINDING]      // Eidolon - all
    };

    const applicableSkills = petRoleToSkills[petData.role] || [];
    const synergies = [];

    // Define which talents give bonus items (matches husbandry.js check*Talents functions)
    const talentBonusItems = {
        [SKILLS.WOODCUTTING]: [
            { talent: 'woodcutting_roots', icon: '🌱', name: 'Seeds (Deep Roots)' },
            { talent: 'woodcutting_kindling', icon: '🔥', name: 'Ashes (Kindling Master)' }
        ],
        [SKILLS.MINING]: [
            { talent: 'mining_gems', icon: '💎', name: 'Gems (Gem Finder)' },
            { talent: 'mining_rare', icon: '🪨', name: 'Ores (Rare Earth)' }
        ],
        [SKILLS.FISHING]: [
            { talent: 'fishing_bycatch', icon: '🦐', name: 'Bycatch' }
        ],
        [SKILLS.GATHERER]: [
            { talent: 'gatherer_seed_finder', icon: '🌱', name: 'Seeds (Seed Finder)' },
            { talent: 'gatherer_awakening', icon: '✨', name: 'Awakening Materials' }
        ],
        [SKILLS.FARMING]: [
            { talent: 'farming_reclaim', icon: '🌱', name: 'Seeds (Reclamation)' },
            { talent: 'farming_abundant', icon: '🌾', name: 'Seeds (Abundant)' }
        ],
        [SKILLS.ALCHEMY]: [
            { talent: 'alchemy_transmute', icon: '🧪', name: 'Upgraded Herbs (Transmute)' }
        ],
        [SKILLS.PYRE]: [
            { talent: 'pyre_smoke', icon: '🔥', name: 'Ashes (Smoke Reading)' },
            { talent: 'pyre_blessing', icon: '🧪', name: 'Potions (Veil\'s Blessing)' }
        ],
        [SKILLS.GLYPHBINDING]: [
            { talent: 'glyphbinding_siphon', icon: '💧', name: 'Ichor (Essence Siphon)' },
            { talent: 'glyphbinding_resonant', icon: '✨', name: 'Glyphs (Resonant)' }
        ]
    };

    for (const skillId of applicableSkills) {
        const skillTalents = talentBonusItems[skillId] || [];
        for (const { talent, icon, name } of skillTalents) {
            if (getNodeRank(skillId, talent) > 0) {
                const skillInfo = skillRegistry.get(skillId);
                synergies.push({
                    icon,
                    name,
                    skillName: skillInfo?.name || skillId,
                    skillIcon: skillRegistry.getSkillIcon(skillId)
                });
            }
        }
    }

    return synergies;
}

/**
 * Render awakening requirements for a pet
 */
function renderEvolutionRequirements(petId, currentEvolution) {
    if (currentEvolution >= 2) {
        return '<span class="max-evolution-text">Max Awakening Reached</span>';
    }

    const costs = getEvolutionCost(petId, currentEvolution);
    if (!costs) return '';

    const costItems = Object.entries(costs).map(([itemId, qty]) => {
        const playerQty = gameState.getItemCount(itemId);
        const hasEnough = playerQty >= qty;
        const itemName = getItemName(itemId) || itemId.replace(/_/g, ' ');
        return `<span class="evo-req ${hasEnough ? 'has-enough' : 'missing'}">
            ${itemName}: ${formatNumber(playerQty)}/${formatNumber(qty)}
        </span>`;
    }).join('');

    return `
        <div class="evolution-requirements">
            <span class="evo-req-label">Next Awakening:</span>
            ${costItems}
        </div>
    `;
}

/**
 * Render inline contract selection for a pet
 */
function renderContractSelection(petId) {
    const availableContracts = getContractsForPet(petId);
    const pet = gameState.getPet(petId);
    const petData = getPetData(petId);
    const speedBonus = husbandrySkill.getPetSpeedBonus(petId);
    const tierLevel = husbandrySkill.getHusbandryTierLevel();

    // Check if patrols are unlocked (2+ unlocked pets)
    const unlockedPetCount = gameState.getPets().filter(p => p.unlocked).length;
    const patrolsUnlocked = unlockedPetCount >= 2;

    const contractsHtml = availableContracts.map(contract => {
        // Apply speed bonus to show effective duration
        const effectiveDuration = contract.duration * (1 - speedBonus);
        const duration = formatDuration(effectiveDuration);

        const typeIcon = getContractTypeIcon(contract.type);

        // Build rewards preview - include tier bonus items
        const tierRewards = getContractTierRewards(contract.type, tierLevel);
        const baseItemIds = new Set(contract.rewards.map(r => r.itemId));

        const rewardsHtml = contract.rewards.map(r => {
            const itemName = getItemName(r.itemId) || r.itemId.replace(/_/g, ' ');
            const itemIcon = getItemIcon(r.itemId);
            const chance = Math.round(r.chance * 100);
            return `<span class="reward-preview" data-item-id="${r.itemId}" data-tooltip="${itemName}">${itemIcon}<span class="reward-chance">${chance}%</span></span>`;
        }).join('');

        // Add tier rewards with special styling
        const tierRewardsHtml = tierRewards.map(r => {
            const itemName = getItemName(r.itemId) || r.itemId.replace(/_/g, ' ');
            const itemIcon = getItemIcon(r.itemId);
            const chance = Math.round(r.chance * 100);
            return `<span class="reward-preview tier-reward" data-item-id="${r.itemId}" data-tooltip="${itemName} (Tier Upgrade)">${itemIcon}<span class="reward-chance">${chance}%</span></span>`;
        }).join('');

        // Show research progress for combat contracts (only if patrols unlocked)
        let researchHtml = '';
        if (patrolsUnlocked && contract.type === 'combat') {
            // Determine research points based on duration
            const durations = PET_CONFIG.CONTRACT_DURATIONS;
            let durationKey = 'short';
            if (contract.duration >= durations.long) {
                durationKey = 'long';
            } else if (contract.duration >= durations.medium) {
                durationKey = 'medium';
            }
            const researchPoints = PET_CONFIG.RESEARCH_PROGRESS[durationKey] || 1;
            researchHtml = `<span class="contract-research" data-tooltip="Research points per contract (assign patrol zone first)">📚 +${researchPoints}</span>`;
        }

        return `
            <div class="inline-contract-option"
                 onclick="window.husbandryUI.assignContract('${petId}', '${contract.id}')">
                <span class="contract-type-icon">${typeIcon}</span>
                <div class="contract-option-info">
                    <span class="contract-option-name">${contract.name}${researchHtml}</span>
                    <span class="contract-option-details">${duration} | ${getContractXPDisplay(contract)}</span>
                    <span class="contract-option-rewards">${rewardsHtml}${tierRewardsHtml}</span>
                </div>
            </div>
        `;
    }).join('');

    return `
        <div class="inline-contract-selection">
            <div class="contract-selection-header">
                <span>Select Contract:</span>
                <button class="close-selection-btn" onclick="window.husbandryUI.toggleContractSelection('${petId}')">&times;</button>
            </div>
            <div class="contract-options-list">
                ${contractsHtml}
            </div>
        </div>
    `;
}

/**
 * Render compact header for Husbandry skill
 * Shows skill info, XP stats, and pet/contract counts instead of tools
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.HUSBANDRY);
    const xpPerHour = getXPPerHour(SKILLS.HUSBANDRY);

    // Level display with virtual levels
    const displayLevel = skill.level;
    const virtualLevel = skill.virtualLevel || skill.level;
    const levelDisplay = virtualLevel > GAME_CONFIG.MAX_LEVEL
        ? `Lv ${displayLevel} <span class="virtual-level">(${virtualLevel})</span>`
        : `Lv ${displayLevel}`;

    // Progress calculation
    const progressPercent = skill.nextLevelXp > 0
        ? Math.floor((skill.currentXp / skill.nextLevelXp) * 100)
        : 100;

    // Pet stats
    const allPets = gameState.getPets();
    const unlockedPets = allPets.filter(p => p.unlocked).length;
    const totalPets = Object.keys(PETS).length;
    const activeContracts = allPets.reduce((count, p) => {
        if (!p.unlocked) return count;
        return count + (p.contractId ? 1 : 0) + (p.secondaryContractId ? 1 : 0);
    }, 0);

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${skillRegistry.getSkillIcon(SKILLS.HUSBANDRY)}</span>
                    <span class="compact-name">Husbandry</span>
                    ${renderSkillInfo(SKILLS.HUSBANDRY)}
                    <span class="compact-level">${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.HUSBANDRY)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-pets">
                    <span class="pets-stat">
                        <span class="stat-label">Pets:</span>
                        <span class="stat-value">${unlockedPets}/${totalPets}</span>
                    </span>
                    <span class="pets-stat active-contracts">
                        <span class="stat-label">Active:</span>
                        <span class="stat-value ${activeContracts > 0 ? 'has-active' : ''}">${activeContracts}</span>
                    </span>
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${progressPercent}%</span>
                </div>
                <span class="compact-progress-text">${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)} XP</span>
            </div>
        </div>
    `;
}

/**
 * Update compact header stats without full re-render
 */
function updateCompactHeader() {
    const skill = gameState.getSkill(SKILLS.HUSBANDRY);
    const xpPerHour = getXPPerHour(SKILLS.HUSBANDRY);

    // Update level
    const levelEl = document.querySelector('.husbandry-interface .compact-level');
    if (levelEl) {
        const virtualLevel = skill.virtualLevel || skill.level;
        levelEl.innerHTML = virtualLevel > GAME_CONFIG.MAX_LEVEL
            ? `Lv ${skill.level} <span class="virtual-level">(${virtualLevel})</span>`
            : `Lv ${skill.level}`;
    }

    // Update XP values
    const xpTotalEl = document.querySelector('.husbandry-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.husbandry-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    // Update progress bar
    const progressPercent = skill.nextLevelXp > 0
        ? Math.floor((skill.currentXp / skill.nextLevelXp) * 100)
        : 100;

    const progressFill = document.querySelector('.husbandry-interface .compact-progress-fill');
    if (progressFill) progressFill.style.width = `${progressPercent}%`;

    const progressPercentEl = document.querySelector('.husbandry-interface .compact-progress-percent');
    if (progressPercentEl) progressPercentEl.textContent = `${progressPercent}%`;

    const progressText = document.querySelector('.husbandry-interface .compact-progress-text');
    if (progressText) progressText.textContent = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)} XP`;

    // Update pet/contract counts
    const allPets = gameState.getPets();
    const unlockedPets = allPets.filter(p => p.unlocked).length;
    const totalPets = Object.keys(PETS).length;
    const activeContracts = allPets.reduce((count, p) => {
        if (!p.unlocked) return count;
        return count + (p.contractId ? 1 : 0) + (p.secondaryContractId ? 1 : 0);
    }, 0);

    const petsStatEl = document.querySelector('.husbandry-interface .compact-pets .pets-stat:not(.active-contracts) .stat-value');
    if (petsStatEl) petsStatEl.textContent = `${unlockedPets}/${totalPets}`;

    const activeStatEl = document.querySelector('.husbandry-interface .active-contracts .stat-value');
    if (activeStatEl) {
        activeStatEl.textContent = activeContracts;
        activeStatEl.classList.toggle('has-active', activeContracts > 0);
    }
}

/**
 * Render a compact pet card for the main grid
 * Shows essential info only - click to open management modal
 */
function renderCompactPetCard(pet, petData) {
    const isLocked = !pet.unlocked;
    const canUnlock = husbandrySkill.canUnlockPet(pet.id);

    if (isLocked) {
        // Build requirements display
        const requirementsList = [`Lv ${petData.unlockLevel}`];
        if (petData.questUnlock) {
            const hasQuestUnlock = gameState.hasQuestUnlock(petData.questUnlock);
            const unlockName = gameState.getUnlockDisplayName(petData.questUnlock);
            const questNum = findQuestByUnlock(petData.questUnlock);
            if (hasQuestUnlock) {
                requirementsList.push(`<span class="req-met">Quest: ${unlockName}</span>`);
            } else if (questNum) {
                requirementsList.push(`<span class="req-missing quest-link" onclick="event.stopPropagation(); window.questModal.navigateToQuest(${questNum})" data-tooltip="Click to view quest">Quest: ${unlockName}</span>`);
            } else {
                requirementsList.push(`<span class="req-missing">Quest: ${unlockName}</span>`);
            }
        }

        // Locked pet card
        return `
            <div class="pet-card-compact locked" data-pet-id="${pet.id}">
                <div class="pet-card-header">
                    <div class="pet-icon-compact" style="background-color: ${petData.color};">
                        🔒
                    </div>
                    <div class="pet-header-info">
                        <span class="pet-name">${petData.name}</span>
                        <span class="pet-tier">Requires ${requirementsList.join(', ')}</span>
                    </div>
                </div>
                <div class="pet-card-body-compact">
                    <p class="pet-description-compact">${petData.description}</p>
                    ${canUnlock.success ? `
                        <button class="pet-action-btn unlock" onclick="window.husbandryUI.unlockPet('${pet.id}'); event.stopPropagation();">
                            Unlock Pet
                        </button>
                    ` : ''}
                </div>
            </div>
        `;
    }

    // Pet is unlocked - show compact card
    const evolutionName = petData.evolutions[pet.evolution];
    const hasContract = !!pet.contractId;
    const hasSecondary = !!pet.secondaryContractId;
    const contract = hasContract ? PET_CONTRACTS[pet.contractId] : null;
    const secondaryContract = hasSecondary ? PET_CONTRACTS[pet.secondaryContractId] : null;
    const progress = hasContract ? husbandrySkill.getPetContractProgress(pet.id, 'primary') : 0;
    const secondaryProgress = hasSecondary ? husbandrySkill.getPetContractProgress(pet.id, 'secondary') : 0;
    const timeRemaining = hasContract ? husbandrySkill.getContractTimeRemaining(pet.id, 'primary') : null;
    const secondaryTimeRemaining = hasSecondary ? husbandrySkill.getContractTimeRemaining(pet.id, 'secondary') : null;
    const isComplete = progress >= 1;
    const isSecondaryComplete = secondaryProgress >= 1;
    const isWaitingForChest = isComplete && !gameState.hasLootChestSpace();
    const isSecondaryWaiting = isSecondaryComplete && !gameState.hasLootChestSpace();
    const isDualEligible = husbandrySkill.canPetDualContract(pet.id);

    const speedBonus = husbandrySkill.getPetSpeedBonus(pet.id);
    const evolutionTier = pet.evolution + 1;
    const tierLevel = husbandrySkill.getHusbandryTierLevel();

    // Food progress
    const evolution = pet.evolution || 0;
    const maxFood = getMaxFood(evolution, pet.id);
    const foodFed = Math.min(pet.foodFed || 0, maxFood);
    const foodPercent = Math.round((foodFed / maxFood) * 100);

    // Contract type icon helper
    const getTypeIcon = (c) => c ? getContractTypeIcon(c.type) : '';

    const typeIcon = getTypeIcon(contract);

    // Render a single contract row (num is only shown when there are dual contracts)
    const renderContractRow = (c, prog, timeRem, complete, waiting, num = null) => {
        const icon = getTypeIcon(c);
        const numLabel = num ? `<span class="contract-num">${num}.</span>` : '';
        return `
            <div class="contract-status-row">
                ${numLabel}
                <span class="contract-type-badge">${icon}</span>
                <span class="contract-status-name">${c.name}</span>
                <span class="contract-status-timer">
                    ${waiting
                        ? '<span class="chest-full-mini">📦</span>'
                        : complete
                            ? '<span class="ready-badge">Ready!</span>'
                            : `⏱ ${formatTimeRemaining(timeRem)}`
                    }
                </span>
            </div>
            <div class="contract-progress-compact">
                <div class="contract-progress-fill-compact ${complete ? 'complete' : ''} ${waiting ? 'waiting' : ''}"
                     style="width: ${Math.min(prog * 100, 100)}%;"></div>
            </div>
        `;
    };

    return `
        <div class="pet-card-compact ${hasContract ? 'on-contract' : ''} ${isComplete || isSecondaryComplete ? 'contract-complete' : ''}"
             data-pet-id="${pet.id}"
             onclick="window.husbandryUI.openPetModal('${pet.id}')">
            <div class="pet-card-header">
                <div class="pet-icon-compact" style="background-color: ${petData.color};">
                    ${petData.icon}
                </div>
                <div class="pet-header-info">
                    <span class="pet-name">${evolutionName}</span>
                    <span class="pet-tier">Tier ${evolutionTier}/3${isDualEligible ? ` · ${(hasContract ? 1 : 0) + (hasSecondary ? 1 : 0)}/2` : ''}</span>
                </div>
                <div class="pet-quick-stats">
                    <span class="quick-stat speed ${speedBonus > 0 ? 'has-bonus' : ''}" data-tooltip="Speed Bonus">
                        ⚡+${Math.round(speedBonus * 100)}%
                    </span>
                </div>
            </div>

            <div class="pet-feed-bar-compact">
                <div class="feed-bar-fill" style="width: ${foodPercent}%;"></div>
            </div>

            ${hasContract ? `
                <div class="pet-contract-status">
                    ${renderContractRow(contract, progress, timeRemaining, isComplete, isWaitingForChest, hasSecondary ? 1 : null)}
                    ${hasSecondary ? renderContractRow(secondaryContract, secondaryProgress, secondaryTimeRemaining, isSecondaryComplete, isSecondaryWaiting, 2) : ''}
                    <div class="contract-rewards-preview">
                        <div class="rewards-label">Rewards:</div>
                        <div class="rewards-grid">
                            ${contract.rewards.map(r => {
                                const itemName = getItemName(r.itemId) || r.itemId.replace(/_/g, ' ');
                                const itemIcon = getItemIcon(r.itemId);
                                const qtyText = r.minQty === r.maxQty ? `${r.minQty}` : `${r.minQty}-${r.maxQty}`;
                                const chance = Math.round(r.chance * 100);
                                return `<span class="reward-preview-item" data-tooltip="${itemName} (${chance}%)">${itemIcon} <span class="reward-qty">x${qtyText}</span></span>`;
                            }).join('')}
                            ${(() => {
                                // Add tier upgrade rewards
                                const tierRewards = getContractTierRewards(contract.type, tierLevel);
                                return tierRewards.map(r => {
                                    const itemName = getItemName(r.itemId) || r.itemId.replace(/_/g, ' ');
                                    const itemIcon = getItemIcon(r.itemId);
                                    const qtyText = r.minQty === r.maxQty ? `${r.minQty}` : `${r.minQty}-${r.maxQty}`;
                                    const chance = Math.round(r.chance * 100);
                                    return `<span class="reward-preview-item tier-reward" data-tooltip="${itemName} (${chance}%) - Tier Upgrade">${itemIcon} <span class="reward-qty">x${qtyText}</span></span>`;
                                }).join('');
                            })()}
                            ${(() => {
                                // Add insight rewards if this pet can get them
                                if (contract.insightChance && contract.insightTiers) {
                                    const insightChance = contract.insightChance[pet.id] || 0;
                                    const insightTiers = contract.insightTiers[pet.id] || [];
                                    if (insightChance > 0 && insightTiers.length > 0) {
                                        const chancePercent = Math.round(insightChance * 100);
                                        return insightTiers.map(insightId => {
                                            const insightName = getItemName(insightId) || insightId.replace(/_/g, ' ');
                                            const insightIcon = getItemIcon(insightId);
                                            return `<span class="reward-preview-item insight" data-tooltip="${insightName} (${chancePercent}%)">${insightIcon}</span>`;
                                        }).join('');
                                    }
                                }
                                return '';
                            })()}
                        </div>
                        <div class="contract-xp-row">
                            <span class="xp-label">${hasSecondary ? '1. ' : ''}XP:</span>
                            <span class="contract-xp-preview">${getContractXPDisplay(contract)}</span>
                        </div>
                        ${hasSecondary ? `
                        <div class="contract-xp-row secondary">
                            <span class="xp-label">2. XP:</span>
                            <span class="contract-xp-preview">${getContractXPDisplay(secondaryContract)}</span>
                        </div>
                        ` : ''}
                    </div>
                </div>
            ` : `
                <div class="pet-no-contract">
                    <span class="no-contract-text">No active contract</span>
                    <span class="no-contract-hint">Click to manage</span>
                </div>
            `}
        </div>
    `;
}

/**
 * Render the pets tab content
 */
function renderPetsTab() {
    const petsWithData = husbandrySkill.getAllPetsWithData();

    const petCards = petsWithData.map(({ pet, data }) => renderCompactPetCard(pet, data)).join('');

    return `
        <div class="pets-section">
            <div class="pets-grid-compact">
                ${petCards}
            </div>
        </div>
    `;
}


/**
 * Render a loot chest slot - bank-style compact squares
 */
function renderChestSlot(slot, index) {
    if (!slot) {
        return `<div class="chest-slot empty"></div>`;
    }

    const itemName = getItemName(slot.itemId) || slot.itemId.replace(/_/g, ' ');
    const itemColor = getItemColor(slot.itemId) || '#555';
    const itemIcon = getItemIcon(slot.itemId, slot.quantity) || '?';

    return `
        <div class="chest-slot filled"
             data-item-id="${slot.itemId}"
             onclick="window.husbandryUI.claimItem('${slot.itemId}', ${slot.quantity})"
             data-tooltip="Click to claim ${formatNumber(slot.quantity)} ${itemName}">
            <div class="chest-slot-content" style="background-color: ${itemColor};">
                <span class="chest-item-icon">${itemIcon}</span>
                <span class="chest-item-qty">${formatNumber(slot.quantity)}</span>
            </div>
        </div>
    `;
}

/**
 * Render chest fill timer section
 */
function renderChestTimer() {
    const timerData = calculateChestFillTimer();
    const maxStack = gameState.getLootChestMaxStack();

    if (timerData.activePets === 0) {
        return `
            <div class="chest-timer">
                <span class="timer-icon"></span>
                <span class="timer-label">No active pets</span>
                <span class="timer-value">--</span>
            </div>
        `;
    }

    // Determine display state - check unique slots for "Full!" detection
    const usedSlots = gameState.getLootChestUniqueCount();
    const maxSlots = gameState.getLootChestMaxSlots();
    const isSlotsFull = usedSlots >= maxSlots;

    let timerClass = '';
    let timerText = '';

    if (isSlotsFull) {
        timerClass = 'full';
        timerText = 'Full!';
    } else if (timerData.timeUntilFull !== null) {
        const seconds = Math.ceil(timerData.timeUntilFull / 1000);
        timerText = formatTime(seconds);
        // Mark as "soon" if less than 30 minutes
        if (timerData.timeUntilFull < 1800000) {
            timerClass = 'soon';
        }
    } else {
        timerText = '--';
    }

    const fillPercent = Math.round((usedSlots / maxSlots) * 100);
    const infoTooltip = renderInfoIcon(
        `Slots: ${usedSlots}/${maxSlots} (${fillPercent}%)\n` +
        `Stack limit: ${maxStack} per slot\n` +
        'Upgrade via Architecting milestones.',
        { size: 'sm' }
    );

    // Show estimate warning only when timer is active
    const showEstimateWarning = timerData.timeUntilFull !== null && !isSlotsFull;
    const estimateTooltip = renderInfoIcon(
        'This is an estimate only.\n' +
        'Actual time may vary due to:\n' +
        '• Stack limits on items\n' +
        '• Contract completion timing\n' +
        '• Items already in chest',
        { size: 'sm' }
    );

    return `
        <div class="chest-timer">
            <span class="timer-icon"></span>
            <span class="timer-label">Est. time until full:</span>
            <span class="timer-value ${timerClass}">${timerText}</span>
            <span class="estimate-tooltip" style="${showEstimateWarning ? '' : 'display: none;'}">${estimateTooltip}</span>
            ${infoTooltip}
        </div>
    `;
}

/**
 * Render the loot chest tab content - bank-style grid layout
 */
function renderChestTab() {
    const chestStats = husbandrySkill.getLootChestStats();
    const chest = chestStats.items;

    // Create slots based on dynamic max (base + Architecting bonuses)
    const slots = [];
    for (let i = 0; i < chestStats.maxSlots; i++) {
        slots.push(chest[i] || null);
    }

    const slotsHtml = slots.map((slot, i) => renderChestSlot(slot, i)).join('');

    // Notification threshold setting
    const currentThreshold = gameState.settings.chestNotificationThreshold || 'any';
    const thresholdOptions = [
        { value: 'off', label: 'Off' },
        { value: 'any', label: 'Any items' },
        { value: 'half', label: 'Half full' },
        { value: 'full', label: 'Completely full' }
    ];

    const thresholdOptionsHtml = thresholdOptions.map(opt => `
        <label class="notification-option ${currentThreshold === opt.value ? 'active' : ''}">
            <input type="radio" name="chest-notification"
                   value="${opt.value}"
                   ${currentThreshold === opt.value ? 'checked' : ''}
                   onchange="window.husbandryUI.setNotificationThreshold('${opt.value}')">
            <span>${opt.label}</span>
        </label>
    `).join('');

    const isChestFull = chestStats.usedSlots >= chestStats.maxSlots;
    const maxStack = gameState.getLootChestMaxStack();
    const isOverflow = chestStats.usedSlots > chestStats.maxSlots;

    return `
        <div class="chest-panel ${isOverflow ? 'overflow' : ''}">
            <div class="chest-panel-header">
                <h3 class="chest-panel-title">📦 Loot Chest${isChestFull ? '<span class="chest-full-badge">⚠️ FULL</span>' : ''}</h3>
                <span class="chest-panel-count ${isOverflow ? 'overflow' : ''}">${chestStats.usedSlots}/${chestStats.maxSlots} slots</span>
                <span class="chest-stack-info">(${maxStack} per item)</span>
            </div>
            ${renderChestTimer()}
            <div class="chest-main">
                <div class="chest-grid">
                    ${slotsHtml}
                </div>
                ${chestStats.usedSlots > 0 ? `
                    <button class="chest-claim-all-btn" onclick="window.husbandryUI.claimAll()">
                        Claim All
                    </button>
                ` : ''}
                <p class="chest-help">Click items to claim to inventory</p>
            </div>

            <div class="chest-notification-settings">
                <h4>Sidebar Notification</h4>
                <p class="settings-desc">Show a red indicator on Husbandry in the sidebar when:</p>
                <div class="notification-options">
                    ${thresholdOptionsHtml}
                </div>
            </div>
        </div>
    `;
}


/**
 * Render the pet management modal - tabbed interface for contracts, feeding, awakening
 */
function renderPetModal() {
    if (!selectedPetId) return '';

    const pet = gameState.getPet(selectedPetId);
    if (!pet || !pet.unlocked) return '';

    const petData = getPetData(selectedPetId);
    const evolution = pet.evolution || 0;
    const evolutionName = petData.evolutions[evolution];
    const speedBonus = husbandrySkill.getPetSpeedBonus(selectedPetId);

    // Food info
    const maxFood = getMaxFood(evolution, selectedPetId);
    const foodFed = Math.min(pet.foodFed || 0, maxFood);
    const foodPercent = Math.round((foodFed / maxFood) * 100);

    // Contract info
    const hasContract = !!pet.contractId;
    const contract = hasContract ? PET_CONTRACTS[pet.contractId] : null;
    const progress = hasContract ? husbandrySkill.getPetContractProgress(selectedPetId, 'primary') : 0;
    const timeRemaining = hasContract ? husbandrySkill.getContractTimeRemaining(selectedPetId, 'primary') : null;
    const isComplete = progress >= 1;
    const isWaitingForChest = isComplete && !gameState.hasLootChestSpace();

    // Secondary contract info
    const hasSecondary = !!pet.secondaryContractId;
    const secondaryContract = hasSecondary ? PET_CONTRACTS[pet.secondaryContractId] : null;
    const secondaryProgress = hasSecondary ? husbandrySkill.getPetContractProgress(selectedPetId, 'secondary') : 0;
    const secondaryTimeRemaining = hasSecondary ? husbandrySkill.getContractTimeRemaining(selectedPetId, 'secondary') : null;
    const isSecondaryComplete = secondaryProgress >= 1;
    const isSecondaryWaiting = isSecondaryComplete && !gameState.hasLootChestSpace();

    return `
        <div class="modal-overlay" onclick="window.husbandryUI.closePetModal()">
            <div class="pet-modal" onclick="event.stopPropagation()">
                <div class="pet-modal-header">
                    <div class="pet-modal-title">
                        <div class="pet-modal-icon" style="background-color: ${petData.color};">
                            ${petData.icon}
                        </div>
                        <div class="pet-modal-info">
                            <h3>${evolutionName}</h3>
                            <span class="pet-modal-tier">Tier ${evolution + 1}/3 • ${petData.role.charAt(0).toUpperCase() + petData.role.slice(1)} Pet</span>
                        </div>
                    </div>
                    <div class="pet-modal-stats">
                        <span class="modal-stat">⚡ +${Math.round(speedBonus * 100)}%</span>
                        <span class="modal-stat">🍖 ${formatNumber(foodFed)}/${formatNumber(maxFood)}</span>
                    </div>
                    <button class="modal-close" onclick="window.husbandryUI.closePetModal()">&times;</button>
                </div>

                <div class="pet-modal-tabs">
                    <button class="pet-modal-tab ${petModalTab === 'contracts' ? 'active' : ''}"
                            onclick="window.husbandryUI.switchPetModalTab('contracts')">
                        📜 Contracts
                    </button>
                    <button class="pet-modal-tab ${petModalTab === 'feed' ? 'active' : ''}"
                            onclick="window.husbandryUI.switchPetModalTab('feed')">
                        🍖 Feed
                    </button>
                    <button class="pet-modal-tab ${petModalTab === 'awaken' ? 'active' : ''}"
                            onclick="window.husbandryUI.switchPetModalTab('awaken')">
                        ✨ Awaken
                    </button>
                </div>

                <div class="pet-modal-content">
                    ${petModalTab === 'contracts' ? renderContractsTab(pet, petData, contract, progress, timeRemaining, isComplete, isWaitingForChest, secondaryContract, secondaryProgress, secondaryTimeRemaining, isSecondaryComplete, isSecondaryWaiting) : ''}
                    ${petModalTab === 'feed' ? renderFeedTab(pet, petData) : ''}
                    ${petModalTab === 'awaken' ? renderAwakenTab(pet, petData) : ''}
                </div>
            </div>
        </div>
    `;
}

/**
 * Render the contracts tab content
 */
function renderContractsTab(pet, petData, currentContract, progress, timeRemaining, isComplete, isWaitingForChest, secondaryContract, secondaryProgress, secondaryTimeRemaining, isSecondaryComplete, isSecondaryWaiting) {
    const availableContracts = getContractsForPet(pet.id);
    const speedBonus = husbandrySkill.getPetSpeedBonus(pet.id);
    const synergies = getPetTalentSynergies(pet.id);
    const isDualEligible = husbandrySkill.canPetDualContract(pet.id);
    const tierLevel = husbandrySkill.getHusbandryTierLevel();

    // Check if patrols are unlocked (2+ unlocked pets)
    const unlockedPetCount = gameState.getPets().filter(p => p.unlocked).length;
    const patrolsUnlocked = unlockedPetCount >= 2;

    // Helper to render a single active contract section
    const renderActiveContract = (contract, prog, timeRem, complete, waiting, slot) => {
        const typeIcon = getContractTypeIcon(contract.type);

        const slotLabel = isDualEligible ? (slot === 'secondary' ? 'Secondary Contract' : 'Primary Contract') : 'Active Contract';

        // Get tier rewards for this contract type
        const activeTierRewards = getContractTierRewards(contract.type, tierLevel);

        return `
            <div class="current-contract-card">
                <div class="current-contract-header">
                    <span class="contract-type-badge">${typeIcon}</span>
                    <span class="current-contract-name">${contract.name}</span>
                    <button class="cancel-contract-btn" onclick="window.husbandryUI.cancelContract('${pet.id}', '${slot}')" data-tooltip="Cancel Contract">✕</button>
                </div>
                <div class="current-contract-rewards">
                    ${contract.rewards.map(r => {
                        const itemName = getItemName(r.itemId) || r.itemId.replace(/_/g, ' ');
                        const itemIcon = getItemIcon(r.itemId);
                        const chance = Math.round(r.chance * 100);
                        return `<span class="reward-item" data-tooltip="${itemName} (${chance}%)">${itemIcon} x${r.minQty}-${r.maxQty}</span>`;
                    }).join('')}
                    ${activeTierRewards.map(r => {
                        const itemName = getItemName(r.itemId) || r.itemId.replace(/_/g, ' ');
                        const itemIcon = getItemIcon(r.itemId);
                        const chance = Math.round(r.chance * 100);
                        return `<span class="reward-item tier-reward" data-tooltip="${itemName} (${chance}%) - Tier Upgrade">${itemIcon} x${r.minQty}-${r.maxQty}</span>`;
                    }).join('')}
                    <span class="reward-xp">${getContractXPDisplay(contract)}</span>
                </div>
                <div class="current-contract-progress">
                    <div class="contract-progress-bar-modal">
                        <div class="contract-progress-fill-modal ${complete ? 'complete' : ''} ${waiting ? 'waiting' : ''}"
                             style="width: ${Math.min(prog * 100, 100)}%;"></div>
                    </div>
                    <div class="contract-progress-info">
                        ${waiting
                            ? `<span class="chest-full-warning">📦 Chest Full!</span>`
                            : complete
                                ? `<button class="collect-btn-modal" onclick="window.husbandryUI.completeContract('${pet.id}', '${slot}')">Collect Rewards!</button>`
                                : `<span class="time-remaining-modal">⏱ ${formatTimeRemaining(timeRem)}</span>`
                        }
                    </div>
                </div>
            </div>
        `;
    };

    // Contract slots indicator for dual-eligible pets
    let slotsHtml = '';
    if (isDualEligible) {
        const usedSlots = (currentContract ? 1 : 0) + (secondaryContract ? 1 : 0);
        slotsHtml = `
            <div class="contract-slots-indicator">
                <span class="slots-icon">📜</span>
                <span class="slots-label">Contract Slots:</span>
                <span class="slots-count ${usedSlots >= 2 ? 'full' : ''}">${usedSlots}/2</span>
                <span class="slots-hint">Eternal Bond: Fully awakened pets can run 2 contracts at once</span>
            </div>
        `;
    }

    let currentContractHtml = '';
    if (currentContract || secondaryContract) {
        const sectionTitle = isDualEligible ? 'Active Contracts' : 'Active Contract';
        currentContractHtml = `
            <div class="current-contract-section">
                <h4>${sectionTitle}</h4>
                ${currentContract ? renderActiveContract(currentContract, progress, timeRemaining, isComplete, isWaitingForChest, 'primary') : ''}
                ${secondaryContract ? renderActiveContract(secondaryContract, secondaryProgress, secondaryTimeRemaining, isSecondaryComplete, isSecondaryWaiting, 'secondary') : ''}
            </div>
        `;
    }

    // Synergies display
    let synergiesHtml = '';
    if (synergies.length > 0) {
        synergiesHtml = `
            <div class="pet-synergies-info">
                <span class="synergy-label">Talent Synergies:</span>
                ${synergies.map(s => `<span class="synergy-badge" data-tooltip="${s.skillName}: ${s.name}">${s.skillIcon} ${s.icon}</span>`).join('')}
            </div>
        `;
    }

    // Group contracts by type (foraging, hunting, purification, veil)
    const durations = PET_CONFIG.CONTRACT_DURATIONS;
    const contractTypes = {
        resource: { label: 'Foraging', icon: '🌿', contracts: [] },
        combat: { label: 'Hunting', icon: '⚔️', contracts: [] },
        purification: { label: 'Purification', icon: '✨', contracts: [] },
        veil: { label: 'Veil Expeditions', icon: '👻', contracts: [] }
    };

    availableContracts.forEach(contract => {
        if (contractTypes[contract.type]) {
            contractTypes[contract.type].contracts.push(contract);
        }
    });

    // Sort contracts within each type by duration (short -> medium -> long)
    Object.values(contractTypes).forEach(type => {
        type.contracts.sort((a, b) => a.duration - b.duration);
    });

    // Render a single contract card
    const renderContractCard = (contract) => {
        const effectiveDuration = contract.duration * (1 - speedBonus);
        const duration = formatDuration(effectiveDuration);

        const typeIcon = getContractTypeIcon(contract.type);

        // Research points for combat contracts
        let researchHtml = '';
        if (patrolsUnlocked && contract.type === 'combat') {
            let durationKey = 'short';
            if (contract.duration >= durations.long) durationKey = 'long';
            else if (contract.duration >= durations.medium) durationKey = 'medium';
            const researchPoints = PET_CONFIG.RESEARCH_PROGRESS[durationKey] || 1;
            researchHtml = `<span class="contract-research" data-tooltip="Patrol Research">📚+${researchPoints}</span>`;
        }

        const isCurrentContract = (currentContract && currentContract.id === contract.id) ||
                                  (secondaryContract && secondaryContract.id === contract.id);

        // Show rewards with item names and quantities
        const rewardsHtml = contract.rewards.map(r => {
            const itemName = getItemName(r.itemId) || r.itemId.replace(/_/g, ' ');
            const itemIcon = getItemIcon(r.itemId);
            const chance = Math.round(r.chance * 100);
            const qtyText = r.minQty === r.maxQty ? `${r.minQty}` : `${r.minQty}-${r.maxQty}`;
            return `<span class="reward-item-detailed" data-tooltip="${itemName} (${chance}%)">
                ${itemIcon} <span class="reward-qty">x${qtyText}</span>
            </span>`;
        }).join('');

        // Add tier upgrade rewards
        const tierRewards = getContractTierRewards(contract.type, tierLevel);
        const tierRewardsHtml = tierRewards.map(r => {
            const itemName = getItemName(r.itemId) || r.itemId.replace(/_/g, ' ');
            const itemIcon = getItemIcon(r.itemId);
            const chance = Math.round(r.chance * 100);
            const qtyText = r.minQty === r.maxQty ? `${r.minQty}` : `${r.minQty}-${r.maxQty}`;
            return `<span class="reward-item-detailed tier-reward" data-tooltip="${itemName} (${chance}%) - Tier Upgrade">
                ${itemIcon} <span class="reward-qty">x${qtyText}</span>
            </span>`;
        }).join('');

        // Add insight rewards if this pet can get them from this contract
        let insightHtml = '';
        if (contract.insightChance && contract.insightTiers) {
            const insightChance = contract.insightChance[pet.id] || 0;
            const insightTiers = contract.insightTiers[pet.id] || [];
            if (insightChance > 0 && insightTiers.length > 0) {
                const chancePercent = Math.round(insightChance * 100);
                const insightIcons = insightTiers.map(insightId => {
                    const insightName = getItemName(insightId) || insightId.replace(/_/g, ' ');
                    const insightIcon = getItemIcon(insightId);
                    return `<span class="insight-icon" data-tooltip="${insightName}">${insightIcon}</span>`;
                }).join('');
                insightHtml = `<span class="reward-item-detailed insight-reward" data-tooltip="Insight drop (${chancePercent}% chance)">
                    ${insightIcons} <span class="reward-qty">${chancePercent}%</span>
                </span>`;
            }
        }

        return `
            <div class="contract-card-compact ${isCurrentContract ? 'current' : ''}"
                 ${isCurrentContract ? '' : `onclick="window.husbandryUI.assignContract('${pet.id}', '${contract.id}')"`}>
                <div class="contract-card-top">
                    <span class="contract-type-icon">${typeIcon}</span>
                    <span class="contract-card-name">${contract.name}</span>
                </div>
                <div class="contract-card-meta">
                    <span class="contract-card-duration">⏱${duration}</span>
                    ${researchHtml}
                </div>
                <div class="contract-card-rewards">${rewardsHtml}${tierRewardsHtml}${insightHtml}</div>
                <div class="contract-card-bottom">
                    <span class="contract-card-xp">${getContractXPDisplay(contract)}</span>
                    ${isCurrentContract ? '<span class="contract-active-badge">Active</span>' : ''}
                </div>
            </div>
        `;
    };

    // Render each contract type row
    const contractsListHtml = Object.entries(contractTypes)
        .filter(([_, type]) => type.contracts.length > 0)
        .map(([key, type]) => `
            <div class="contract-tier-row">
                <div class="contract-tier-label">
                    <span class="tier-icon">${type.icon}</span>
                    <span class="tier-name">${type.label}</span>
                </div>
                <div class="contract-tier-cards">
                    ${type.contracts.map(renderContractCard).join('')}
                </div>
            </div>
        `).join('');

    return `
        ${slotsHtml}
        ${currentContractHtml}
        ${synergiesHtml}
        <div class="available-contracts-section">
            <h4>${currentContract && secondaryContract ? 'Switch Contract' : currentContract && isDualEligible ? 'Add Secondary Contract' : currentContract ? 'Switch Contract' : 'Available Contracts'}</h4>
            <div class="contracts-list">
                ${contractsListHtml}
            </div>
        </div>
    `;
}

/**
 * Render the feed tab content
 */
function renderFeedTab(pet, petData) {
    const evolution = pet.evolution || 0;
    const maxFood = getMaxFood(evolution, pet.id);
    const foodFed = pet.foodFed || 0;
    const remainingFood = Math.max(0, maxFood - foodFed);
    const foodPercent = Math.round((foodFed / maxFood) * 100);
    const canUnlockMore = evolution < 2;

    const requiredFood = getRequiredFood(evolution, pet.id);
    const foodId = requiredFood.foodId;
    const playerQty = gameState.getItemCount(foodId);
    const foodName = getItemName(foodId) || foodId.replace(/_/g, ' ');
    const foodColor = getItemColor(foodId) || '#555';
    const foodIcon = getItemIcon(foodId);

    const speedBonus = husbandrySkill.getPetSpeedBonus(pet.id);
    const canFeed = playerQty > 0 && remainingFood > 0;
    const actualMaxFood = Math.min(playerQty, remainingFood);
    const feedTen = Math.min(10, actualMaxFood);
    const feedFifty = Math.min(50, actualMaxFood);

    const tierNames = ['Tier 1', 'Tier 2', 'Tier 3'];

    return `
        <div class="feed-tab-content">
            <div class="feed-status-section">
                <div class="feed-progress-large">
                    <div class="feed-bar-large">
                        <div class="feed-fill-large ${remainingFood <= 0 ? 'maxed' : ''}" style="width: ${foodPercent}%;"></div>
                    </div>
                    <div class="feed-labels">
                        <span class="feed-count">${formatNumber(foodFed)} / ${formatNumber(maxFood)}</span>
                        <span class="feed-remaining">${remainingFood > 0 ? `${formatNumber(remainingFood)} remaining` : 'Fully Fed!'}</span>
                    </div>
                </div>
                <div class="speed-bonus-display">
                    <span class="speed-label">Current Speed Bonus</span>
                    <span class="speed-value ${speedBonus > 0 ? 'has-bonus' : ''}">+${Math.round(speedBonus * 100)}%</span>
                </div>
            </div>

            <div class="feed-info-section">
                <p>Feed your pet to increase contract speed. Higher tiers unlock more capacity!</p>
                <p class="feed-tier-requirement">${tierNames[evolution]} pets require <strong>${foodName}</strong></p>
                ${canUnlockMore ? `<p class="evolve-hint">Awaken to unlock higher tier food capacity!</p>` : ''}
            </div>

            <div class="feed-action-section">
                <div class="food-display" style="border-left: 4px solid ${foodColor};">
                    <span class="food-icon">${foodIcon}</span>
                    <div class="food-info">
                        <span class="food-name">${foodName}</span>
                        <span class="food-qty">You have: ${formatNumber(playerQty)}</span>
                    </div>
                </div>
                <div class="feed-buttons">
                    <button class="feed-btn ${feedTen > 0 ? '' : 'disabled'}"
                            onclick="window.husbandryUI.feedPet('${pet.id}', ${feedTen})"
                            ${feedTen > 0 ? '' : 'disabled'}>
                        +${feedTen}
                    </button>
                    <button class="feed-btn ${feedFifty > 10 ? '' : 'disabled'}"
                            onclick="window.husbandryUI.feedPet('${pet.id}', ${feedFifty})"
                            ${feedFifty > 10 ? '' : 'disabled'}>
                        +${feedFifty}
                    </button>
                    <button class="feed-btn all ${actualMaxFood > 0 ? '' : 'disabled'}"
                            onclick="window.husbandryUI.feedPet('${pet.id}', ${actualMaxFood})"
                            ${actualMaxFood > 0 ? '' : 'disabled'}>
                        All (+${formatNumber(actualMaxFood)})
                    </button>
                </div>
            </div>
        </div>
    `;
}

/**
 * Render the awaken tab content
 */
function renderAwakenTab(pet, petData) {
    const evolution = pet.evolution || 0;
    const canEvolve = husbandrySkill.canEvolvePet(pet.id);
    const isMaxEvolution = evolution >= 2;

    // Get actual bonuses for next evolution
    const nextEvolution = evolution + 1;
    const nextEvoData = EVOLUTION_BONUSES[nextEvolution] || {};
    const currentEvoData = EVOLUTION_BONUSES[evolution] || {};

    // Speed bonus from evolution itself
    const speedBonusGain = Math.round((nextEvoData.speedBonus - currentEvoData.speedBonus) * 100);

    // Food capacity increase
    const currentMaxFood = getMaxFood(evolution, pet.id);
    const nextMaxFood = getMaxFood(nextEvolution, pet.id);
    const foodCapacityGain = nextMaxFood - currentMaxFood;

    // New food type
    const nextFoodId = FEED_CONFIG.EVOLUTION_FOOD_TYPE[nextEvolution];
    const nextFoodName = nextFoodId ? (getItemName(nextFoodId) || nextFoodId.replace(/_/g, ' ')) : '';

    // Max feeding bonus increase
    const currentMaxFeedBonus = Math.round((FEED_CONFIG.EVOLUTION_MAX_BONUS[evolution] || 0) * 100);
    const nextMaxFeedBonus = Math.round((FEED_CONFIG.EVOLUTION_MAX_BONUS[nextEvolution] || 0) * 100);

    // Get costs for next evolution
    const costs = getEvolutionCost(pet.id, evolution);

    let requirementsHtml = '';
    if (costs && !isMaxEvolution) {
        requirementsHtml = Object.entries(costs).map(([itemId, qty]) => {
            const playerQty = gameState.getItemCount(itemId);
            const hasEnough = playerQty >= qty;
            const itemName = getItemName(itemId) || itemId.replace(/_/g, ' ');
            const itemIcon = getItemIcon(itemId);
            return `
                <div class="evo-requirement ${hasEnough ? 'met' : 'missing'}">
                    <span class="evo-req-icon">${itemIcon}</span>
                    <span class="evo-req-name">${itemName}</span>
                    <span class="evo-req-count">${formatNumber(playerQty)} / ${formatNumber(qty)}</span>
                </div>
            `;
        }).join('');
    }

    // Evolution tier preview
    const tiers = petData.evolutions.map((name, i) => {
        const isCurrent = i === evolution;
        const isUnlocked = i <= evolution;
        return `
            <div class="evo-tier ${isCurrent ? 'current' : ''} ${isUnlocked ? 'unlocked' : 'locked'}">
                <span class="tier-number">Tier ${i + 1}</span>
                <span class="tier-name">${name}</span>
            </div>
        `;
    }).join('');

    return `
        <div class="awaken-tab-content">
            <div class="evolution-tiers">
                ${tiers}
            </div>

            ${isMaxEvolution ? `
                <div class="max-evolution-notice">
                    <span class="max-evo-icon">✨</span>
                    <span class="max-evo-text">Maximum Awakening Achieved!</span>
                    <p>This pet has reached its full potential.</p>
                </div>
            ` : `
                <div class="next-evolution-section">
                    <h4>Awaken to ${petData.evolutions[evolution + 1]}</h4>
                    <div class="evo-bonuses">
                        <span class="bonus-label">Bonuses:</span>
                        <div class="bonus-list">
                            <span class="bonus-item">⚡ +${speedBonusGain}% Base Contract Speed</span>
                            <span class="bonus-item">🍖 +${formatNumber(foodCapacityGain)} Food Capacity (${formatNumber(nextMaxFood)} total)</span>
                            <span class="bonus-item">📈 Max Feed Bonus: ${currentMaxFeedBonus}% → ${nextMaxFeedBonus}%</span>
                            <span class="bonus-item">🥩 New Food: ${nextFoodName}</span>
                        </div>
                    </div>
                    <div class="evo-requirements">
                        <span class="req-label">Requirements:</span>
                        ${requirementsHtml}
                    </div>
                    <button class="awaken-btn ${canEvolve.success ? '' : 'disabled'}"
                            onclick="window.husbandryUI.evolvePet('${pet.id}')"
                            ${canEvolve.success ? '' : `data-tooltip="${canEvolve.reason}"`}>
                        ${canEvolve.success ? 'Awaken Pet' : canEvolve.reason}
                    </button>
                </div>
            `}
        </div>
    `;
}

/**
 * Render tab navigation
 */
function renderTabs() {
    const chestStats = husbandrySkill.getLootChestStats();
    const hasChestNotification = shouldShowChestNotification();

    // Check if chest is full (slots full OR at max capacity)
    const maxStack = gameState.getLootChestMaxStack();
    const totalCapacity = chestStats.maxSlots * maxStack;
    const chest = gameState.getLootChest();
    const currentFill = chest.reduce((sum, slot) => sum + slot.quantity, 0);
    const isChestFull = chestStats.usedSlots >= chestStats.maxSlots || currentFill >= totalCapacity;

    // Determine tab notification: FULL (urgent) > ! (items to collect)
    let chestTabNotification = '';
    if (isChestFull) {
        chestTabNotification = '<span class="tab-notification warning">FULL</span>';
    } else if (hasChestNotification) {
        chestTabNotification = '<span class="tab-notification">!</span>';
    }

    return `
        <div class="skill-tabs">
            <button class="skill-tab ${currentTab === 'pets' ? 'active' : ''}"
                    onclick="window.husbandryUI.switchTab('pets')">
                🐾 Pets
            </button>
            <button class="skill-tab ${currentTab === 'chest' ? 'active' : ''}"
                    onclick="window.husbandryUI.switchTab('chest')">
                📦 Loot Chest
                ${chestTabNotification}
            </button>
        </div>
    `;
}

/**
 * Check if chest notification should show based on settings
 */
function shouldShowChestNotification() {
    const threshold = gameState.settings.chestNotificationThreshold || 'any';
    if (threshold === 'off') return false;

    const chestStats = husbandrySkill.getLootChestStats();
    const maxStack = gameState.getLootChestMaxStack();
    const totalCapacity = chestStats.maxSlots * maxStack;
    const chest = gameState.getLootChest();
    const currentFill = chest.reduce((sum, slot) => sum + slot.quantity, 0);
    const fillPercent = currentFill / totalCapacity;

    // Check if all slots are full (can't add new item types)
    const allSlotsFull = chestStats.usedSlots >= chestStats.maxSlots;
    // Check if at max capacity (all slots at max stack)
    const atMaxCapacity = currentFill >= totalCapacity;

    switch (threshold) {
        case 'any':
            return chestStats.usedSlots > 0;
        case 'half':
            return fillPercent >= 0.5;
        case 'full':
            // Trigger when either all slots are used OR at max capacity
            return allSlotsFull || atMaxCapacity;
        default:
            return false;
    }
}

/**
 * Main render function
 */
export function renderHusbandryUI(params = {}) {
    // Handle tab from route params
    if (params.tab) {
        currentTab = params.tab;
    }

    // If tab is 'contracts' (old tab), redirect to pets
    if (currentTab === 'contracts') {
        currentTab = 'pets';
    }

    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.HUSBANDRY);

    let tabContent;
    switch (currentTab) {
        case 'chest':
            tabContent = renderChestTab();
            break;
        default:
            tabContent = renderPetsTab();
    }

    container.innerHTML = `
        <div class="husbandry-interface">
            ${renderCompactHeader()}

            <div class="husbandry-content">
                ${renderTabs()}

                <div class="tab-content">
                    ${tabContent}
                </div>
            </div>
        </div>
        ${renderPetModal()}
    `;
}

/**
 * Update UI each tick - update contract progress bars and timers
 */
export function updateHusbandryUI() {
    // Update compact header
    updateCompactHeader();

    // Update pet contract progress bars and timers if on pets tab
    if (currentTab === 'pets') {
        const pets = gameState.getPets();

        for (const pet of pets) {
            if (!pet.unlocked) continue;
            if (!pet.contractId && !pet.secondaryContractId) continue;

            const card = document.querySelector(`.pet-card-compact[data-pet-id="${pet.id}"]`);
            if (!card) continue;

            const progressFills = card.querySelectorAll('.contract-progress-fill-compact');
            const timerEls = card.querySelectorAll('.contract-status-timer');

            // Update primary (index 0)
            if (pet.contractId) {
                const progress = husbandrySkill.getPetContractProgress(pet.id, 'primary');
                const timeRemaining = husbandrySkill.getContractTimeRemaining(pet.id, 'primary');
                const isComplete = progress >= 1;

                if (progressFills[0]) {
                    progressFills[0].style.width = `${Math.min(progress * 100, 100)}%`;
                    if (isComplete) progressFills[0].classList.add('complete');
                }
                if (timerEls[0] && !isComplete) {
                    timerEls[0].innerHTML = `⏱ ${formatTimeRemaining(timeRemaining)}`;
                } else if (isComplete && timerEls[0]) {
                    // Only re-render if chest has space (contract can actually complete)
                    // If waiting for chest space, skip re-render to prevent flicker
                    if (gameState.hasLootChestSpace()) {
                        renderHusbandryUI();
                        return;
                    }
                }
            }

            // Update secondary (index 1)
            if (pet.secondaryContractId) {
                const progress = husbandrySkill.getPetContractProgress(pet.id, 'secondary');
                const timeRemaining = husbandrySkill.getContractTimeRemaining(pet.id, 'secondary');
                const isComplete = progress >= 1;

                if (progressFills[1]) {
                    progressFills[1].style.width = `${Math.min(progress * 100, 100)}%`;
                    if (isComplete) progressFills[1].classList.add('complete');
                }
                if (timerEls[1] && !isComplete) {
                    timerEls[1].innerHTML = `⏱ ${formatTimeRemaining(timeRemaining)}`;
                } else if (isComplete && timerEls[1]) {
                    // Only re-render if chest has space (contract can actually complete)
                    // If waiting for chest space, skip re-render to prevent flicker
                    if (gameState.hasLootChestSpace()) {
                        renderHusbandryUI();
                        return;
                    }
                }
            }
        }
    }

    // Update modal if open
    if (selectedPetId) {
        const pet = gameState.getPet(selectedPetId);
        if (pet && (pet.contractId || pet.secondaryContractId)) {
            const modalProgressFills = document.querySelectorAll('.contract-progress-fill-modal');
            const modalTimerEls = document.querySelectorAll('.time-remaining-modal');

            // Update primary modal progress
            if (pet.contractId && modalProgressFills[0]) {
                const progress = husbandrySkill.getPetContractProgress(selectedPetId, 'primary');
                const timeRemaining = husbandrySkill.getContractTimeRemaining(selectedPetId, 'primary');
                const isComplete = progress >= 1;

                modalProgressFills[0].style.width = `${Math.min(progress * 100, 100)}%`;
                if (isComplete) {
                    modalProgressFills[0].classList.add('complete');
                    // Only re-render if chest has space to prevent flicker when waiting
                    if (gameState.hasLootChestSpace()) {
                        renderHusbandryUI();
                        return;
                    }
                }
                if (modalTimerEls[0] && !isComplete) {
                    modalTimerEls[0].textContent = `⏱ ${formatTimeRemaining(timeRemaining)}`;
                }
            }

            // Update secondary modal progress
            if (pet.secondaryContractId && modalProgressFills[1]) {
                const progress = husbandrySkill.getPetContractProgress(selectedPetId, 'secondary');
                const timeRemaining = husbandrySkill.getContractTimeRemaining(selectedPetId, 'secondary');
                const isComplete = progress >= 1;

                modalProgressFills[1].style.width = `${Math.min(progress * 100, 100)}%`;
                if (isComplete) {
                    modalProgressFills[1].classList.add('complete');
                    // Only re-render if chest has space to prevent flicker when waiting
                    if (gameState.hasLootChestSpace()) {
                        renderHusbandryUI();
                        return;
                    }
                }
                if (modalTimerEls[1] && !isComplete) {
                    modalTimerEls[1].textContent = `⏱ ${formatTimeRemaining(timeRemaining)}`;
                }
            }
        }
    }

    // Update chest stats if on chest tab
    if (currentTab === 'chest') {
        const chestStats = husbandrySkill.getLootChestStats();
        const usedEl = document.querySelector('.chest-panel-count');
        if (usedEl) {
            usedEl.textContent = `${chestStats.usedSlots}/${chestStats.maxSlots} slots`;
        }

        // Update timer - check unique slots for "Full!" detection
        const timerData = calculateChestFillTimer();
        const usedSlots = gameState.getLootChestUniqueCount();
        const maxSlots = gameState.getLootChestMaxSlots();
        const isSlotsFull = usedSlots >= maxSlots;

        const timerValueEl = document.querySelector('.chest-timer .timer-value');
        if (timerValueEl) {
            let timerText = '--';
            let timerClass = '';

            if (timerData.activePets === 0) {
                timerText = '--';
            } else if (isSlotsFull) {
                timerClass = 'full';
                timerText = 'Full!';
            } else if (timerData.timeUntilFull !== null) {
                const seconds = Math.ceil(timerData.timeUntilFull / 1000);
                timerText = formatTime(seconds);
                if (timerData.timeUntilFull < 1800000) {
                    timerClass = 'soon';
                }
            }

            timerValueEl.textContent = timerText;
            timerValueEl.className = `timer-value ${timerClass}`;
        }

        // Update no active pets label if needed
        const timerLabelEl = document.querySelector('.chest-timer .timer-label');
        if (timerLabelEl) {
            if (timerData.activePets === 0) {
                timerLabelEl.textContent = 'No active pets';
            } else {
                timerLabelEl.textContent = 'Est. time until full:';
            }
        }
    }
}

/**
 * Initialize UI and set up callbacks
 */
export function initHusbandryUI() {
    // Set up contract completion callback
    husbandrySkill.onContractComplete = (petId, contract, xpResult, rewards, restartedContract) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Husbandry level ${xpResult.newLevel}!`);
        }

        // Re-render if on husbandry skill and talent tree is not open
        if (getCurrentSkillId() === SKILLS.HUSBANDRY && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderHusbandryUI();
            }
        }
    };
}

// ==================== UI EVENT HANDLERS ====================

export function switchTab(tab) {
    currentTab = tab;
    renderHusbandryUI();
}

export function unlockPet(petId) {
    const result = husbandrySkill.unlockPet(petId);
    if (!result.success) {
        toastWarning(result.reason);
    } else {
        const petData = getPetData(petId);
        toastSuccess(`${petData.name} unlocked!`);
    }
    renderHusbandryUI();
}

export function evolvePet(petId) {
    const result = husbandrySkill.evolvePet(petId);
    if (!result.success) {
        toastWarning(result.reason);
        return;
    }

    const pet = gameState.getPet(petId);
    const petData = getPetData(petId);
    toastSuccess(`${petData.evolutions[pet.evolution]} awakened!`);

    // Update modal content and pet card without full re-render
    updatePetModalContent();
    updatePetCard(petId);
}

export function openPetModal(petId) {
    selectedPetId = petId;
    petModalTab = 'contracts'; // Default to contracts tab
    renderHusbandryUI();
}

export function closePetModal() {
    selectedPetId = null;
    renderHusbandryUI();
}

export function switchPetModalTab(tab) {
    petModalTab = tab;
    updatePetModalContent();
}

/**
 * Update just the modal content and tab states (no flicker)
 */
function updatePetModalContent() {
    if (!selectedPetId) return;

    const pet = gameState.getPet(selectedPetId);
    if (!pet || !pet.unlocked) return;

    const petData = getPetData(selectedPetId);

    // Update tab button states
    const tabButtons = document.querySelectorAll('.pet-modal-tab');
    tabButtons.forEach(btn => {
        const tabName = btn.textContent.includes('Contracts') ? 'contracts' :
                        btn.textContent.includes('Feed') ? 'feed' : 'awaken';
        btn.classList.toggle('active', tabName === petModalTab);
    });

    // Update content area
    const contentArea = document.querySelector('.pet-modal-content');
    if (!contentArea) return;

    // Get contract info for contracts tab
    const hasContract = !!pet.contractId;
    const contract = hasContract ? PET_CONTRACTS[pet.contractId] : null;
    const progress = hasContract ? husbandrySkill.getPetContractProgress(selectedPetId, 'primary') : 0;
    const timeRemaining = hasContract ? husbandrySkill.getContractTimeRemaining(selectedPetId, 'primary') : null;
    const isComplete = progress >= 1;
    const isWaitingForChest = isComplete && !gameState.hasLootChestSpace();

    // Secondary contract info
    const hasSecondary = !!pet.secondaryContractId;
    const secondaryContract = hasSecondary ? PET_CONTRACTS[pet.secondaryContractId] : null;
    const secondaryProgress = hasSecondary ? husbandrySkill.getPetContractProgress(selectedPetId, 'secondary') : 0;
    const secondaryTimeRemaining = hasSecondary ? husbandrySkill.getContractTimeRemaining(selectedPetId, 'secondary') : null;
    const isSecondaryComplete = secondaryProgress >= 1;
    const isSecondaryWaiting = isSecondaryComplete && !gameState.hasLootChestSpace();

    let newContent = '';
    if (petModalTab === 'contracts') {
        newContent = renderContractsTab(pet, petData, contract, progress, timeRemaining, isComplete, isWaitingForChest, secondaryContract, secondaryProgress, secondaryTimeRemaining, isSecondaryComplete, isSecondaryWaiting);
    } else if (petModalTab === 'feed') {
        newContent = renderFeedTab(pet, petData);
    } else if (petModalTab === 'awaken') {
        newContent = renderAwakenTab(pet, petData);
    }

    contentArea.innerHTML = newContent;
}

export function assignContract(petId, contractId) {
    const result = husbandrySkill.assignContract(petId, contractId);
    if (!result.success) {
        toastWarning(result.reason);
        return;
    }
    const contract = PET_CONTRACTS[contractId.toUpperCase()];
    toastSuccess(`Started ${contract.name}!`);

    // Update modal content and pet card without full re-render
    updatePetModalContent();
    updatePetCard(petId);
}

export function cancelContract(petId, slot = 'primary') {
    const result = husbandrySkill.stopContract(petId, slot);
    if (!result.success) {
        toastWarning(result.reason);
        return;
    }
    toastSuccess(`${slot === 'secondary' ? 'Secondary c' : 'C'}ontract cancelled`);

    // Update modal content and pet card without full re-render
    updatePetModalContent();
    updatePetCard(petId);
}

export function completeContract(petId, slot = 'primary') {
    const result = husbandrySkill.completeContract(petId, slot);
    if (!result.success) {
        toastWarning(result.reason);
        return;
    }

    // Update modal content and pet card without full re-render
    updatePetModalContent();
    updatePetCard(petId);
}

/**
 * Update just a single pet card without re-rendering the whole UI
 */
function updatePetCard(petId) {
    const cardEl = document.querySelector(`.pet-card-compact[data-pet-id="${petId}"]`);
    if (!cardEl) return;

    const pet = gameState.getPet(petId);
    const petData = getPetData(petId);
    if (!pet || !petData) return;

    // Create new card HTML and replace
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = renderCompactPetCard(pet, petData);
    const newCard = tempDiv.firstElementChild;

    if (newCard) {
        cardEl.replaceWith(newCard);
    }
}

export function claimItem(itemId, amount) {
    const result = husbandrySkill.claimFromChest(itemId, amount);
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderHusbandryUI();
}

export function claimAll() {
    const chest = husbandrySkill.getLootChest();
    const hadItems = chest && chest.length > 0;

    const result = husbandrySkill.claimAllFromChest();

    // If chest had items but nothing was claimed, inventory is full
    if (hadItems && result.claimedItems.length === 0) {
        toastWarning('Inventory full');
    } else if (result.claimedItems.length > 0) {
        // Golden Opportunity: chance for bonus gold when claiming husbandry chest
        rollGoldenOpportunity(result.claimedItems);
    }

    renderHusbandryUI();
}

// Legacy function names for backwards compatibility
export function openFeedModal(petId) {
    selectedPetId = petId;
    petModalTab = 'feed';
    renderHusbandryUI();
}

export function closeFeedModal() {
    closePetModal();
}

export function feedPet(petId, amount) {
    const result = husbandrySkill.feedPet(petId, amount);
    if (!result.success) {
        toastWarning(result.reason);
        return;
    }

    const foodName = getItemName(result.foodId) || result.foodId.replace(/_/g, ' ');
    toastSuccess(`Fed +${formatNumber(result.foodUsed)} ${foodName}!`);

    // Update modal content and pet card without full re-render
    updatePetModalContent();
    updatePetCard(petId);
}

export function setNotificationThreshold(threshold) {
    gameState.settings.chestNotificationThreshold = threshold;
    // Update sidebar notification immediately
    updateSidebarNotification();
    renderHusbandryUI();
}

/**
 * Check if any pet is waiting for chest space (at 100% but can't complete)
 */
function anyPetWaitingForChest() {
    const pets = gameState.getPets();
    for (const pet of pets) {
        if (!pet.unlocked) continue;
        if (pet.contractId && husbandrySkill.isPetWaitingForChest(pet.id, 'primary')) return true;
        if (pet.secondaryContractId && husbandrySkill.isPetWaitingForChest(pet.id, 'secondary')) return true;
    }
    return false;
}

/**
 * Update the sidebar notification indicator for Husbandry
 */
export function updateSidebarNotification() {
    const skillItem = document.querySelector('[data-skill="husbandry"]');
    if (!skillItem) return;

    const hasNotification = shouldShowChestNotification();
    const petsWaiting = anyPetWaitingForChest();
    const existingIndicator = skillItem.querySelector('.skill-notification');

    // Priority: pets waiting (warning) > chest items (regular)
    if (petsWaiting) {
        if (existingIndicator) {
            existingIndicator.className = 'skill-notification warning';
            existingIndicator.textContent = '📦';
        } else {
            const indicator = document.createElement('span');
            indicator.className = 'skill-notification warning';
            indicator.textContent = '📦';
            skillItem.appendChild(indicator);
        }
    } else if (hasNotification) {
        if (existingIndicator) {
            existingIndicator.className = 'skill-notification';
            existingIndicator.textContent = '!';
        } else {
            const indicator = document.createElement('span');
            indicator.className = 'skill-notification';
            indicator.textContent = '!';
            skillItem.appendChild(indicator);
        }
    } else if (existingIndicator) {
        existingIndicator.remove();
    }
}

// Export window bindings
if (typeof window !== 'undefined') {
    window.husbandryUI = {
        switchTab,
        unlockPet,
        evolvePet,
        openPetModal,
        closePetModal,
        switchPetModalTab,
        assignContract,
        cancelContract,
        completeContract,
        claimItem,
        claimAll,
        openFeedModal,
        closeFeedModal,
        feedPet,
        setNotificationThreshold
    };
}

export default {
    renderHusbandryUI,
    updateHusbandryUI,
    initHusbandryUI,
    switchTab,
    unlockPet,
    evolvePet,
    openPetModal,
    closePetModal,
    switchPetModalTab,
    assignContract,
    cancelContract,
    completeContract,
    claimItem,
    claimAll,
    openFeedModal,
    closeFeedModal,
    feedPet,
    setNotificationThreshold,
    updateSidebarNotification,
    shouldShowChestNotification
};
